import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { logout } from '../utils/auth';

export interface TrainingStepSummary {
  id: number;
  totalDuration: number;
  isActive: boolean;
  createdAt: string | null;
  finishedAt: string | null;
  order: Array<{ exerciseId: number; typedExerciseId: number; type: string }>;
  MachineTypeExercise: Array<{ id: number; exercise: { id: number; name: string; duration: number } }>;
  CardioTypeExercise: Array<{ id: number; exercise: { id: number; name: string; duration: number } }>;
  StrengthTypeExercise: Array<{ id: number; exercise: { id: number; name: string; duration: number } }>;
  OtherTypeExercise: Array<{ id: number; exercise: { id: number; name: string; duration: number } }>;
  ConsultationTypeExercise: Array<{ id: number; exercise: { id: number; name: string; duration: number } }>;
  machineParams?: any;
}

export interface TrainingSectionsResponseAPI {
  training: { id: number; title: string; subtitle?: string; description?: string; mediaUrl?: string };
  sections: TrainingStepSummary[];
  userTrainingId: number;
  activeStepId: number | null;
}

export interface TrainingDetail {
  id: number;
  title: string;
  subtitle?: string;
  description?: string;
  activeStepId?: number | null;
  finished?: boolean;
  sessions: Array<{
    id: string;
    title: string;
    durationMinutes: number;
    isCompleted: boolean;
    createdAt?: string | null;
    finishedAt?: string | null;
    exercises: Array<{ name: string; done?: boolean; skipped?: boolean }>;
  }>;
  completedSessions?: Record<string, string | Date>;
}

const queryOptions = (userTrainingId: number) => ({
  queryKey: ['training-sessions', userTrainingId],
  queryFn: async () => {
    if (!userTrainingId) throw new Error('No training id');
    const apiKey = localStorage.getItem('apiKey');
    try {
      const { data } = await axios.get<TrainingSectionsResponseAPI>(
        `${process.env.REACT_APP_STUDIO_API_URL}/pwa/user-training/sections?trainingId=${userTrainingId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      //console.log('[useTrainingSessions] raw sections', data);

      const sessions = data.sections.map((section, idx) => {
        const namesById: Record<number, string> = {};
        const durationsById: Record<number, number> = {};
        [...section.MachineTypeExercise, ...section.CardioTypeExercise, ...section.StrengthTypeExercise, ...section.OtherTypeExercise, ...section.ConsultationTypeExercise].forEach((t) => {
          namesById[t.exercise.id] = t.exercise.name;
          durationsById[t.exercise.id] = t.exercise.duration || 0;
        });
        // Build set of completed exercises by (exerciseId, typedExerciseId) and extract real values
        const completedKeys = new Set<string>();
        const realValuesMap = new Map<string, any>();
        const mp = (section as any).machineParams;
        if (Array.isArray(mp)) {
          mp.forEach((p: any) => {
            if (p && typeof p.exerciseId === 'number' && typeof p.typedExerciseId === 'number') {
              const key = `${p.exerciseId}:${p.typedExerciseId}`;
              completedKeys.add(key);
              
              // Extract the real values from the appropriate exercise type object
              const extractedRealValues = {
                machineType: p.machineType || null,
                machinePowerBasedType: p.machinePowerBasedType || null,
                cardioType: p.cardioType || null,
                strengthType: p.strengthType || null,
                otherType: p.otherType || null,
                consultationType: p.consultationType || null
              };
              
              realValuesMap.set(key, extractedRealValues);
            }
          });
        }
        const recomputedTotal = (section.order || []).reduce((sum, o) => sum + (durationsById[o.exerciseId] || 0), 0);
        const durationMinutes = section.totalDuration && section.totalDuration > 0
          ? Math.max(1, Math.round(section.totalDuration))
          : Math.max(1, Math.round((recomputedTotal || 0) / 60));
        const exercises = section.order.map((o) => {
          const key = `${o.exerciseId}:${o.typedExerciseId}`;
          const isCompleted = completedKeys.has(key);
          const realValues = realValuesMap.get(key);
          
          return {
            name: namesById[o.exerciseId] || `Exercise ${o.exerciseId}`,
            done: isCompleted,
            skipped: false,
            // Embed typed-exercise metadata so the session page can fetch details (parameters, media, description)
            _typed: { typedExerciseId: o.typedExerciseId, type: o.type, trainingStepId: section.id, exerciseId: o.exerciseId },
            // Include real values if exercise is completed
            realValues: isCompleted ? realValues : null
          };
        });
        return {
          id: String(section.id),
          title: `Sesja ${idx + 1}`,
          durationMinutes,
          isCompleted: !!section.finishedAt,
          createdAt: section.createdAt,
          finishedAt: section.finishedAt,
          exercises,
        };
      });

      const mapped = {
        id: data.training.id,
        title: data.training.title,
        subtitle: data.training.subtitle,
        description: data.training.description,
        activeStepId: data.activeStepId,
        finished: sessions.every((s) => s.isCompleted),
        sessions,
        completedSessions: Object.fromEntries(
          data.sections
            .filter((s) => !!s.finishedAt)
            .map((s) => [String(s.id), s.createdAt as string])
        ),
      } as TrainingDetail;
      //console.log('[useTrainingSessions] mapped', mapped);
      return mapped;
    } catch (error: any) {
      if (error.response && error.response.status === 401 && error.response.data.error === 'Invalid or missing API key') {
        logout();
      }
      throw error;
    }
  },
  enabled: !!userTrainingId,
}) as any;

export const useTrainingSessions = (userTrainingId: number) => {
  return useQuery<TrainingDetail, Error>(queryOptions(userTrainingId));
};


